/**
 * ユーザー管理サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - GET /api/admin/users - ユーザー一覧取得
 * - POST /api/admin/users/invite - ユーザー招待（招待メール送信）
 * - PUT /api/admin/users/:id - ユーザー情報更新（権限変更など）
 * - DELETE /api/admin/users/:id - ユーザー削除（論理削除）
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type { User, UserRole, UserStatus } from '../../types';

// バックエンドのレスポンス型（snake_case）
interface BackendUserResponse {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'reviewer' | 'user';
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface BackendUsersListResponse {
  data: BackendUserResponse[];
}

interface BackendUserUpdateResponse {
  data: BackendUserResponse;
}

// ユーザーレスポンスの変換（snake_case -> camelCase）
function convertUserResponse(backendUser: BackendUserResponse): User {
  return {
    id: backendUser.id,
    email: backendUser.email,
    name: backendUser.name,
    role: backendUser.role,
    status: backendUser.status,
    createdAt: backendUser.created_at,
    updatedAt: backendUser.updated_at,
  };
}

// ユーザー更新リクエスト型
export interface UserUpdateRequest {
  name?: string;
  role?: UserRole;
  status?: UserStatus;
}

// ユーザー招待リクエスト型
export interface UserInviteRequest {
  email: string;
  role: UserRole;
}

export const userService = {
  /**
   * ユーザー一覧取得
   * GET /api/admin/users
   */
  async getUsers(): Promise<User[]> {
    try {
      const response = await apiClient.get<BackendUsersListResponse>('/api/admin/users');
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertUserResponse);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ユーザー招待（招待メール送信）
   * POST /api/admin/users/invite
   */
  async inviteUser(email: string, role: UserRole): Promise<void> {
    try {
      await apiClient.post('/api/admin/users/invite', { email, role });
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ユーザー情報更新（権限変更など）
   * PUT /api/admin/users/:id
   */
  async updateUser(userId: string, data: UserUpdateRequest): Promise<User> {
    try {
      const response = await apiClient.put<BackendUserUpdateResponse>(
        `/api/admin/users/${userId}`,
        data
      );
      return convertUserResponse(response.data.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ユーザー権限変更
   * PUT /api/admin/users/:id（roleのみ更新）
   */
  async updateUserRole(userId: string, role: UserRole): Promise<User> {
    return this.updateUser(userId, { role });
  },

  /**
   * ユーザー削除（論理削除）
   * DELETE /api/admin/users/:id
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/admin/users/${userId}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default userService;
